import React, { Suspense, useEffect, useState } from 'react'
import { useNotifyStore } from '../../context'
import { NOTIFY_ACTION } from '../../context/action'
import {
    Background,
    CloseIcon,
    DropdownContainerBodyWrapper,
    DropdownContainerWrapper,
    HeaderTitle,
    NotifyBadge,
    NotifyIcon,
    TitleContent
} from './NotificationMain.css'
import ReactDOM from 'react-dom'
import { getJobInvitationNotifications } from '../../services/jobInvitation'
import { isPhone } from '../../helpers/utils'
import { ELanguage } from '../../../Utils/constants'
import * as Images from '../../../Images'
import Typography from '../../../Typography'
import useFloating from '../../../hooks/useFloating'
import { colors } from '../../../Colors'
// Lazy import
const NotificationBody = React.lazy(() => import('../NotificationBody'))

const MAX_DISPLAY_NOTIFY_COUNT = 9

const NotificationMain = () => {
    const { state, firebase, props, t, dispatch } = useNotifyStore()
    const { isOpen, notificationCount } = state

    const [isMounted, setMounted] = useState<boolean>(false)
    const isInMobile = isPhone()

    const { refs, context, FloatingPortal } = useFloating({
        placement: 'bottom-end',
        position: 'fixed',
        config: {
            useWindowAsFlippingContainer: true,
            isFlip: false,
            detectHidden: false
        }
    })

    const onOpenNotification = () => {
        if (isOpen) {
            dispatch({
                type: NOTIFY_ACTION.HIDE
            })
            return
        }

        firebase.getNotification()
        dispatch({
            type: NOTIFY_ACTION.SHOW
        })
    }

    const onClose = () => {
        dispatch({
            type: NOTIFY_ACTION.HIDE
        })
    }

    const onAnimationEnd = () => {
        if (isOpen) {
            return
        }
        setMounted(false)
    }

    const fetchJobInvitationNotifications = async () => {
        if (!props.config?.baseMsUrl || props.config?.baseMsUrl === '') {
            return
        }
        if (!props.user?.accessToken || props.user?.accessToken === '') {
            return
        }
        const responseNotifications = await getJobInvitationNotifications({
            msBaseUrl: props.config?.baseMsUrl,
            accessToken: props.user?.accessToken,
            langCode: props.langCode ?? ELanguage.VI
        })
        if (!responseNotifications) {
            return
        }
        dispatch({
            type: NOTIFY_ACTION.MAP_JOB_INVITATION_NOTIFICATIONS,
            payload: responseNotifications
        })
    }

    useEffect(() => {
        if (!isOpen) {
            return
        }
        const onKeyUp = (e: KeyboardEvent) => {
            if (!isOpen) {
                return
            }
            if (e.key === 'Escape') {
                onClose()
            }
        }

        setMounted(true)
        fetchJobInvitationNotifications()
        window.addEventListener('keyup', onKeyUp)

        return () => {
            window.removeEventListener('keyup', onKeyUp)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    useEffect(() => {
        window.addEventListener('VNW_OPEN_NOTIFICATION', onOpenNotification)

        return () => {
            window.removeEventListener('VNW_OPEN_NOTIFICATION', onOpenNotification)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const dropdownStyle = {
        position: context.position,
        bottom: 0,
        right: 0,
        display: context.isReferenceHidden ? 'none' : 'block',
        zIndex: 201
    }

    const desktopRedDotContainer =
        typeof document !== 'undefined' ? document.querySelector('#desktopRedDotContainer') : null
    const mobileRedDotContainer =
        typeof document !== 'undefined' ? document.querySelector('#mobileRedDotContainer') : null

    let labelOfNumberNotify = `${notificationCount}`
    // If current number of notification is larger than MAX_DISPLAY_NOTIFY_COUNT
    // then we should display it as 'MAX_DISPLAY_NOTIFY_COUNT+'
    if (notificationCount > MAX_DISPLAY_NOTIFY_COUNT) {
        labelOfNumberNotify = `${MAX_DISPLAY_NOTIFY_COUNT}+`
    }

    if (!labelOfNumberNotify) {
        labelOfNumberNotify = '0'
    }

    return (
        <>
            <FloatingPortal enable>
                {isMounted ? (
                    <>
                        <Background onClick={onClose} />
                        <DropdownContainerWrapper
                            isShow={isOpen}
                            ref={refs.setPopup}
                            style={dropdownStyle}
                            onAnimationEnd={onAnimationEnd}
                        >
                            <DropdownContainerBodyWrapper
                                isShow={isOpen}
                                isMobile={isInMobile}
                                data-floating-placement='bottom-end'
                            >
                                <HeaderTitle>
                                    <NotifyIcon>
                                        <Images.BellIcon />
                                    </NotifyIcon>
                                    <TitleContent>
                                        <Typography name='title' size='large' color='#000'>
                                            {t('Notifications')}
                                        </Typography>
                                        <Typography size='medium' color={colors.grey[40]}>
                                            {t('Get notifications for jobs')}
                                        </Typography>
                                    </TitleContent>
                                    <CloseIcon onClick={onClose}>
                                        <Images.CloseIcon />
                                    </CloseIcon>
                                </HeaderTitle>
                                <Suspense fallback={<></>}>
                                    <NotificationBody />
                                </Suspense>
                            </DropdownContainerBodyWrapper>
                        </DropdownContainerWrapper>
                    </>
                ) : (
                    <></>
                )}
            </FloatingPortal>
            {/* Render the Red Dot for DESKTOP */}
            {desktopRedDotContainer && notificationCount > 0
                ? ReactDOM.createPortal(
                      <NotifyBadge className='custom__red_dot totalNumber'>{labelOfNumberNotify}</NotifyBadge>,
                      desktopRedDotContainer
                  )
                : null}

            {/* Render the Red Dot for MOBILE */}
            {mobileRedDotContainer && notificationCount > 0
                ? ReactDOM.createPortal(
                      // UI change, replace red dot to total notification
                      <NotifyBadge className='totalNumber'>{labelOfNumberNotify}</NotifyBadge>,
                      mobileRedDotContainer
                  )
                : null}
        </>
    )
}

export default NotificationMain
